/**
 * Image optimization utilities for better performance
 */

// Preload critical images
export const preloadImage = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = src;
  });
};

// Preload multiple images
export const preloadImages = async (sources: string[]): Promise<void> => {
  try {
    await Promise.all(sources.map(preloadImage));
    console.log('✅ Critical images preloaded successfully');
  } catch (error) {
    console.warn('⚠️ Some images failed to preload:', error);
  }
};

// Critical images to preload
export const CRITICAL_IMAGES = [
  '/logo-appines.png'
];

// Lazy loading intersection observer
export const createLazyLoadObserver = (callback: (entry: IntersectionObserverEntry) => void) => {
  if (!('IntersectionObserver' in window)) {
    // Fallback for older browsers
    return null;
  }

  return new IntersectionObserver(
    (entries) => {
      entries.forEach(callback);
    },
    {
      rootMargin: '50px 0px', // Start loading 50px before the image enters viewport
      threshold: 0.01
    }
  );
};

// Image format detection and optimization
export const getOptimizedImageUrl = (originalUrl: string, width?: number, quality = 85): string => {
  // For external images (Pexels, etc.), return as-is
  if (originalUrl.startsWith('http')) {
    return originalUrl;
  }

  // For local images, we could implement WebP conversion or resizing
  // This is a placeholder for future image optimization service
  let optimizedUrl = originalUrl;

  // Add width parameter if specified
  if (width) {
    const url = new URL(optimizedUrl, window.location.origin);
    url.searchParams.set('w', width.toString());
    url.searchParams.set('q', quality.toString());
    optimizedUrl = url.toString();
  }

  return optimizedUrl;
};

// Responsive image srcSet generation
export const generateSrcSet = (baseUrl: string, sizes: number[]): string => {
  return sizes
    .map(size => `${getOptimizedImageUrl(baseUrl, size)} ${size}w`)
    .join(', ');
};

// Image loading performance metrics
export const trackImageLoadTime = (src: string, startTime: number) => {
  const loadTime = performance.now() - startTime;
  
  // Track in Google Analytics if available
  if (window.gtag) {
    window.gtag('event', 'image_load_time', {
      event_category: 'performance',
      event_label: src,
      value: Math.round(loadTime)
    });
  }
  
  console.log(`📊 Image loaded: ${src} (${Math.round(loadTime)}ms)`);
};